/* ============================================================
   SCNT global behaviours
   1. Header scrolled state
   2. Scroll reveals (.scnt-reveal / .scnt-stagger)
   3. Rotating announcement bar
   4. Hero crossfade slider
   5. Horizontal product carousels (scroll-snap + arrows)
   6. Tabs
   7. Gentle parallax
   ============================================================ */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Header scrolled state ---------- */
  function onScroll() {
    document.body.classList.toggle('scnt-scrolled', window.scrollY > 30);
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------- 2. Scroll reveals ---------- */
  function initReveals(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('.scnt-reveal:not(.is-visible), .scnt-stagger:not(.is-visible)');
    if (!els.length) return;

    if (!('IntersectionObserver' in window) || reducedMotion) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 3. Rotating announcement bar ---------- */
  function initAnnouncements(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.scnt-announcement[data-rotate]:not([data-init])').forEach(function (bar) {
      bar.setAttribute('data-init', '');
      var items = bar.querySelectorAll('.scnt-announcement__item');
      if (items.length < 2) return;
      var i = 0;
      var delay = (parseInt(bar.dataset.interval, 10) || 5) * 1000;
      var timer = null;

      function show(n) {
        items[i].classList.remove('is-active');
        i = (n + items.length) % items.length;
        items[i].classList.add('is-active');
      }
      function start() {
        if (reducedMotion) return;
        stop();
        timer = setInterval(function () { show(i + 1); }, delay);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      start();
    });
  }

  /* ---------- 4. Hero crossfade slider ---------- */
  function initHeroSliders(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.scnt-hero[data-slider]:not([data-init])').forEach(function (hero) {
      hero.setAttribute('data-init', '');
      var slides = hero.querySelectorAll('.scnt-hero__slide');
      var dots = hero.querySelectorAll('.scnt-hero__dot');
      if (slides.length < 2) return;

      var i = 0;
      var delay = (parseInt(hero.dataset.autoplay, 10) || 7) * 1000;
      var timer = null;

      function go(n) {
        slides[i].classList.remove('is-active');
        if (dots[i]) dots[i].classList.remove('is-active');
        i = (n + slides.length) % slides.length;
        slides[i].classList.add('is-active');
        if (dots[i]) dots[i].classList.add('is-active');
      }
      function start() {
        if (reducedMotion) return;
        stop();
        timer = setInterval(function () { go(i + 1); }, delay);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      dots.forEach(function (dot, n) {
        dot.addEventListener('click', function () { go(n); start(); });
      });
      var prev = hero.querySelector('.scnt-hero__arrow--prev');
      var next = hero.querySelector('.scnt-hero__arrow--next');
      if (prev) prev.addEventListener('click', function () { go(i - 1); start(); });
      if (next) next.addEventListener('click', function () { go(i + 1); start(); });

      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);
      start();
    });
  }

  /* ---------- 5. Horizontal carousels ---------- */
  function initCarousels(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.scnt-carousel:not([data-init])').forEach(function (wrap) {
      wrap.setAttribute('data-init', '');
      var track = wrap.querySelector('.scnt-carousel__track');
      var prev = wrap.querySelector('.scnt-carousel__arrow--prev');
      var next = wrap.querySelector('.scnt-carousel__arrow--next');
      if (!track) return;

      function step() {
        var card = track.querySelector(':scope > *');
        if (!card) return 320;
        var gap = parseFloat(getComputedStyle(track).columnGap || 0) || 24;
        return card.getBoundingClientRect().width + gap;
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.toggleAttribute('disabled', track.scrollLeft <= 2);
        if (next) next.toggleAttribute('disabled', track.scrollLeft >= max);
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- 6. Tabs ---------- */
  function initTabs(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.scnt-tabs:not([data-init])').forEach(function (tabs) {
      tabs.setAttribute('data-init', '');
      var btns = tabs.querySelectorAll('.scnt-tabs__btn');
      var holder = document.getElementById(tabs.dataset.panels || '') || tabs.parentElement;

      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          btns.forEach(function (b) { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          holder.querySelectorAll('.scnt-tabpanel').forEach(function (p) {
            p.classList.toggle('is-active', p.id === btn.dataset.target);
          });
          /* refresh arrow state of the carousel inside the newly shown panel */
          var active = holder.querySelector('.scnt-tabpanel.is-active .scnt-carousel__track');
          if (active) active.dispatchEvent(new Event('scroll'));
        });
      });
    });
  }

  /* ---------- 7. Gentle parallax ---------- */
  var parallaxEls = [];
  function collectParallax(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.scnt-parallax:not([data-init])').forEach(function (el) {
      el.setAttribute('data-init', '');
      parallaxEls.push(el);
    });
  }
  var ticking = false;
  function onParallaxScroll() {
    if (reducedMotion || !parallaxEls.length || ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;
      parallaxEls.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var speed = parseFloat(el.dataset.parallaxSpeed || 0.08);
        var offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.setProperty('--scnt-parallax', offset.toFixed(1));
      });
      ticking = false;
    });
  }
  window.addEventListener('scroll', onParallaxScroll, { passive: true });

  /* ---------- 8. Product: price + size chip row ----------
     Wraps the price element and the size picker into one flex row,
     mirroring the live DOM edit. Idempotent and safe across Dawn's
     variant updates (which swap the inner content of #price-* and
     variant-selects, not the moved elements themselves). */
  function initProductPriceRow(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.product__info-container').forEach(function (info) {
      if (info.querySelector('.scnt-price-pill-row')) return;
      var priceEl = info.querySelector('[id^="price-"]');
      var variants = info.querySelector('variant-selects');
      if (!priceEl || !variants) return;
      var row = document.createElement('div');
      row.className = 'scnt-price-pill-row';
      priceEl.parentNode.insertBefore(row, priceEl);
      row.appendChild(priceEl);
      row.appendChild(variants);
    });
  }

  /* ---------- 9. Services seamless marquee ----------
     Duplicates the service items once and wraps both sets in a
     marquee track so the CSS animation loops seamlessly. Idempotent
     via the data-marquee-init guard (won't double-run on section reload). */
  function initServicesMarquee(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.scnt-services__row:not([data-marquee-init])').forEach(function (row) {
      row.setAttribute('data-marquee-init', '');

      // original items only (ignore any prior clones)
      var originals = Array.prototype.filter.call(row.children, function (c) {
        return c.classList.contains('scnt-services__item') && !c.dataset.clone;
      });
      if (!originals.length) return;

      var viewport = document.createElement('div');
      viewport.className = 'scnt-marquee-viewport';
      var track = document.createElement('div');
      track.className = 'scnt-marquee-track';

      // Set A: the real items
      originals.forEach(function (o) { track.appendChild(o); });

      // Set B: an aria-hidden duplicate so the loop point is identical
      originals.forEach(function (o) {
        var clone = o.cloneNode(true);
        clone.dataset.clone = '1';
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a,button,input').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      });

      viewport.appendChild(track);
      row.appendChild(viewport);
    });
  }

  /* ---------- 10. Product description: read more / read less ----------
     Collapses the relocated story paragraph to a few lines with a toggle.
     Clamps the inner <p> (reliable for -webkit-line-clamp) and only adds
     the button when the text actually overflows. Idempotent via guard. */
  function initDescriptionToggle(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var COLLAPSED_LINES = 2; // change to taste

    scope.querySelectorAll('.scnt-moved-prose-p:not([data-readmore-init])').forEach(function (wrap) {
      wrap.setAttribute('data-readmore-init', '');

      var prose = wrap.querySelector('.metafield-rich_text_field p')
        || wrap.querySelector('.metafield-rich_text_field')
        || wrap.querySelector('p')
        || wrap;
      if (!prose || !prose.textContent.trim()) return;

      // start collapsed
      prose.classList.add('scnt-desc', 'scnt-desc--collapsed');
      prose.style.setProperty('--scnt-desc-lines', COLLAPSED_LINES);

      // only show the toggle if the text is actually clamped
      if (prose.scrollHeight <= prose.clientHeight + 1) {
        prose.classList.remove('scnt-desc--collapsed');
        return;
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'scnt-desc-toggle';
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = 'Read the description';

      btn.addEventListener('click', function () {
        var collapsed = prose.classList.toggle('scnt-desc--collapsed');
        btn.setAttribute('aria-expanded', String(!collapsed));
        btn.textContent = collapsed ? 'Read the description' : 'Read less';
      });

      wrap.appendChild(btn);
    });
  }

  /* ---------- boot ---------- */
  function init(root) {
    onScroll();
    initReveals(root);
    initAnnouncements(root);
    initHeroSliders(root);
    initCarousels(root);
    initTabs(root);
    collectParallax(root);
    onParallaxScroll();
    initProductPriceRow(root);
    initServicesMarquee(root);
    initDescriptionToggle(root);
  }

  if (document.readyState !== 'loading') init(document);
  else document.addEventListener('DOMContentLoaded', function () { init(document); });

  /* Theme editor: re-run when a section is added/reloaded */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
